import { useState } from 'react'
import { Ticket, UserRole } from '../types'
import { respondToPropertyMoveTicket, rejectPropertyMoveTicketByReferent } from '../lib/apiPhpAdapter'
import { highlightTextMultiple } from '../utils/textHighlight'
import { DetailsModal } from './DetailsModal'
import './PropertyMoveTicketList.css'

interface PropertyMoveTicketListProps {
  tickets: Ticket[]
  currentUserId?: string
  onTicketUpdate?: () => void
  userRole?: UserRole | null
  onStatusChange?: (ticketId: string, newStatus: 'new' | 'in_progress' | 'completed' | 'organize_transport') => Promise<void>
  searchText?: string
}

function PropertyMoveTicketList({ tickets, currentUserId, onTicketUpdate, userRole, onStatusChange, searchText = '' }: PropertyMoveTicketListProps) {
  const [processingTicketId, setProcessingTicketId] = useState<string | null>(null)
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectTicketId, setRejectTicketId] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  if (tickets.length === 0) {
    return (
      <div className="property-ticket-list-empty">
        <p>Žádné žádosti o přesun majetku</p>
      </div>
    )
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new':
        return 'Nová'
      case 'in_progress':
        return 'V řešení'
      case 'completed':
        return 'Dokončeno'
      case 'rejected':
        return 'Zamítnuto'
      case 'recipient_needs_transport':
        return 'Čeká na převoz'
      case 'recipient_accepted_self_transport':
        return 'Příjemce vyřeší sám'
      case 'awaiting_transport':
        return 'Čeká na převoz IT'
      default:
        return status
    }
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'status-new'
      case 'in_progress':
        return 'status-in-progress'
      case 'completed':
        return 'status-completed'
      case 'rejected':
        return 'status-rejected'
      case 'recipient_needs_transport':
        return 'status-new'
      case 'recipient_accepted_self_transport':
        return 'status-in-progress'
      case 'awaiting_transport':
        return 'status-in-progress'
      default:
        return ''
    }
  }

  const handleCardClick = (ticket: Ticket) => {
    setSelectedTicket(ticket)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedTicket(null)
  }

  const handleRejectCancel = () => {
    setShowRejectModal(false)
    setRejectTicketId(null)
    setRejectionReason('')
  }

  const handleRejectConfirm = async () => {
    if (!rejectTicketId || !rejectionReason.trim()) {
      alert('Prosím vyplňte důvod zamítnutí')
      return
    }

    try {
      setProcessingTicketId(rejectTicketId)
      await rejectPropertyMoveTicketByReferent(rejectTicketId, rejectionReason.trim())
      alert('Žádost byla zamítnuta')
      setShowRejectModal(false)
      setRejectTicketId(null)
      setRejectionReason('')
      onTicketUpdate?.()
    } catch (error) {
      console.error('Error rejecting ticket:', error)
      alert('Chyba při zamítnutí žádosti')
    } finally {
      setProcessingTicketId(null)
    }
  }

  return (
    <>
    <div className="property-ticket-list">
      {tickets.map(ticket => {
        // Проверяем, является ли текущий пользователь получателем
        const isRecipient = currentUserId && ticket.assigned_to_id === currentUserId
        
        // Проверяем, замітнута ли заявка
        const isRejected = ticket.status === 'rejected'
        
        console.log(`Ticket ${ticket.id.substring(0, 8)}: currentUserId=${currentUserId}, assigned_to_id=${ticket.assigned_to_id}, isRecipient=${isRecipient}, isRejected=${isRejected}`)
        
        // Определяем стиль карточки
        // Проверяем, была ли заявка когда-либо замітнута
        const wasRejected = ticket.status === 'rejected' || (ticket as any).rejection_reason
        
        let cardStyle = {}
        if (wasRejected && isRecipient) {
          // Замітнутая И получатель - желтый фон + красная рамка
          cardStyle = {
            border: '4px solid #ef4444',
            backgroundColor: '#fef08a',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
          }
        } else if (wasRejected) {
          // Замітнутая (всегда красная рамка, даже если zpracováno)
          cardStyle = {
            border: '4px solid #ef4444',
            backgroundColor: '#fef2f2',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
          }
        } else if (isRecipient) {
          // Только получатель - желтые
          cardStyle = {
            border: '3px solid #eab308',
            backgroundColor: '#fef08a',
            boxShadow: '0 4px 12px rgba(234, 179, 8, 0.4)'
          }
        }
        
        return (
        <div 
          key={ticket.id} 
          id={`ticket-${ticket.id}`}
          className="property-ticket-card"
          style={{ ...cardStyle, cursor: 'pointer' }}
          onClick={() => handleCardClick(ticket)}
        >
          <div className="ticket-header">
            <h3>
              {ticket.item_name ? (
                <>
                  {highlightTextMultiple(ticket.item_name, searchText)}
                  <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#64748b', marginLeft: '0.5rem' }}>
                    #{ticket.id.substring(0, 8)}
                  </span>
                </>
              ) : (
                <>Žádost #{ticket.id.substring(0, 8)}</>
              )}
              {isRecipient && (
                <span style={{ 
                  marginLeft: '0.5rem', 
                  fontSize: '0.85rem', 
                  color: '#ca8a04',
                  fontWeight: 'bold',
                  backgroundColor: '#fef9c3',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px'
                }}>
                  📬 Vy jste příjemce
                </span>
              )}
            </h3>
            <span className={`status-badge ${getStatusClass(ticket.status)}`}>
              {highlightTextMultiple(getStatusLabel(ticket.status), searchText)}
            </span>
          </div>
          
          <div className="ticket-details">
            <div className="detail-row">
              <strong>Žadatel:</strong> {highlightTextMultiple(ticket.requester_name || 'Neznámý', searchText)} 
              {ticket.requester_email && ` (${highlightTextMultiple(ticket.requester_email, searchText)})`}
            </div>
            <div className="detail-row">
              <strong>Od:</strong> {highlightTextMultiple(ticket.from_location || 'Neznámé', searchText)} 
              {ticket.from_building_name && ` (${highlightTextMultiple(ticket.from_building_name, searchText)})`}
            </div>
            {ticket.item_name && (
              <div className="detail-row">
                <strong>Název předmětu:</strong> {highlightTextMultiple(ticket.item_name, searchText)}
              </div>
            )}
            <div className="detail-row">
              <strong>Inventární číslo:</strong> {highlightTextMultiple(ticket.inventory_number || 'Neznámé', searchText)}
            </div>
            {ticket.is_warehouse ? (
              <>
                <div className="detail-row">
                  <strong>Kam:</strong> Sklad
                </div>
                {ticket.assigned_to_name && (
                  <div className="detail-row">
                    <strong>Komu:</strong> {highlightTextMultiple(ticket.assigned_to_name, searchText)} 
                    {ticket.assigned_to_email && ` (${highlightTextMultiple(ticket.assigned_to_email, searchText)})`}
                  </div>
                )}
                {(ticket as any).requester_transport_option && (
                  <div className="detail-row" style={{ fontWeight: 'bold', color: (ticket as any).requester_transport_option === 'organize' ? '#3b82f6' : '#10b981' }}>
                    {(ticket as any).requester_transport_option === 'self' ? '🚗 Žadatel přiveze sám' : '🚚 Požadován odvoz'}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="detail-row">
                  <strong>Kam:</strong> {highlightTextMultiple(ticket.to_location || 'Neznámé', searchText)}
                  {ticket.to_building_name && ` (${highlightTextMultiple(ticket.to_building_name, searchText)})`}
                </div>
                {ticket.assigned_to_name && (
                  <div className="detail-row">
                    <strong>Komu:</strong> {highlightTextMultiple(ticket.assigned_to_name, searchText)} 
                    {ticket.assigned_to_email && ` (${highlightTextMultiple(ticket.assigned_to_email, searchText)})`}
                  </div>
                )}
              </>
            )}
            <div className="detail-row">
              <strong>Vytvořeno:</strong> {new Date(ticket.created_at).toLocaleString('cs-CZ')}
            </div>
            {ticket.confirmed_received_at && (
              <div className="detail-row" style={{ color: '#28a745', fontWeight: 'bold' }}>
                ✓ Potvrzeno přijetí: {new Date(ticket.confirmed_received_at).toLocaleString('cs-CZ')}
              </div>
            )}
            {(ticket as any).recipient_response && (
              <div className="detail-row" style={{ fontWeight: 'bold', marginTop: '0.5rem' }}>
                Odpověď příjemce: {
                  (ticket as any).recipient_response === 'accepted_self_transport' ? '✅ Přijato, přesun vyřeším sám' :
                  (ticket as any).recipient_response === 'needs_transport' ? '🚚 Přijato, prosím zorganizovat převoz' :
                  (ticket as any).recipient_response === 'rejected' ? '❌ Odmítnuto' : ''
                }
              </div>
            )}
            {(ticket as any).rejection_reason && (
              <div className="detail-row" style={{ color: '#dc2626', fontStyle: 'italic' }}>
                Důvod odmítnutí: {highlightTextMultiple((ticket as any).rejection_reason, searchText)}
              </div>
            )}
            {ticket.transport_ticket_id && (
              <div className="detail-row" style={{ color: '#3b82f6', fontWeight: 'bold', marginTop: '0.5rem' }}>
                🚚 Vytvořena žádost na IT: #{ticket.transport_ticket_id.substring(0, 8)}
                {ticket.transport_ticket_status && ` (${ticket.transport_ticket_status})`}
              </div>
            )}
            {ticket.processed_at && (
              <div className="detail-row" style={{ color: '#10b981', fontWeight: 'bold' }}>
                ✅ Zpracováno: {new Date(ticket.processed_at).toLocaleString('cs-CZ')}
              </div>
            )}
          </div>
          
          {/* Кнопки для получателя - только если он еще не ответил И НЕ является референтом маетка */}
          {/* Если пользователь одновременно референт маетка и получатель, показываем только кнопки референта */}
          {isRecipient && userRole !== 'property_referent' && !ticket.confirmed_received_at && !(ticket as any).recipient_response && (
            <div className="ticket-actions" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  if (processingTicketId) return
                  setProcessingTicketId(ticket.id)
                  try {
                    await respondToPropertyMoveTicket(ticket.id, 'accepted_self_transport')
                    alert('Děkujeme! Referent majetku byl informován.')
                    onTicketUpdate?.()
                  } catch (error) {
                    console.error('Error:', error)
                    alert('Chyba při zpracování odpovědi')
                  } finally {
                    setProcessingTicketId(null)
                  }
                }}
                disabled={processingTicketId === ticket.id}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: processingTicketId === ticket.id ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  opacity: processingTicketId === ticket.id ? 0.6 : 1
                }}
              >
                ✅ Souhlasím přijmout, přesun vyřeším sám
              </button>
              
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  if (processingTicketId) return
                  setProcessingTicketId(ticket.id)
                  try {
                    await respondToPropertyMoveTicket(ticket.id, 'needs_transport')
                    alert('Děkujeme! Referent majetku zorganizuje převoz.')
                    onTicketUpdate?.()
                  } catch (error) {
                    console.error('Error:', error)
                    alert('Chyba při zpracování odpovědi')
                  } finally {
                    setProcessingTicketId(null)
                  }
                }}
                disabled={processingTicketId === ticket.id}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: processingTicketId === ticket.id ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  opacity: processingTicketId === ticket.id ? 0.6 : 1
                }}
              >
                🚚 Souhlasím, prosím zorganizovat převoz
              </button>
              
              <button
                onClick={async (e) => {
                  e.stopPropagation()
                  if (processingTicketId) return
                  const reason = prompt('Prosím uveďte důvod odmítnutí:')
                  if (!reason) return
                  
                  setProcessingTicketId(ticket.id)
                  try {
                    await respondToPropertyMoveTicket(ticket.id, 'rejected', reason)
                    alert('Vaše odmítnutí bylo odesláno referentovi majetku.')
                    onTicketUpdate?.()
                  } catch (error) {
                    console.error('Error:', error)
                    alert('Chyba při zpracování odpovědi')
                  } finally {
                    setProcessingTicketId(null)
                  }
                }}
                disabled={processingTicketId === ticket.id}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: processingTicketId === ticket.id ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  opacity: processingTicketId === ticket.id ? 0.6 : 1
                }}
              >
                ❌ Nesouhlasím přijmout
              </button>
            </div>
          )}
          
          {/* Кнопки для референта маетку */}
          {userRole === 'property_referent' && onStatusChange && (
            <div className="ticket-actions" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
              {/* Кнопка для организации отвоза - для новой логики (requester_transport_option = 'organize') */}
              {(ticket as any).requester_transport_option === 'organize' && 
               !ticket.transport_ticket_id && 
               (ticket.status === 'new' || ticket.status === 'awaiting_recipient_response') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm('Opravdu chcete zorganizovat odvoz na sklad?')) {
                      onStatusChange(ticket.id, 'organize_transport')
                    }
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  🚚 Zorganizovat odvoz na sklad
                </button>
              )}
              
              {/* Старая кнопка для recipient_needs_transport */}
              {ticket.status === 'recipient_needs_transport' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm('Opravdu chcete zorganizovat převoz?')) {
                      onStatusChange(ticket.id, 'in_progress')
                    }
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  🚚 Zorganizovat převoz
                </button>
              )}
              
              {/* Кнопка Замитнуть - для всех статусов кроме rejected и completed */}
              {ticket.status !== 'rejected' && ticket.status !== 'completed' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm('Opravdu chcete zamítnout tuto žádost?')) {
                      setRejectTicketId(ticket.id)
                      setShowRejectModal(true)
                      setRejectionReason('')
                    }
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  ❌ Zamítnout
                </button>
              )}
              
              {/* Кнопка Zpracováno - только для заявок на склад ИЛИ после подтверждения получателя ИЛИ после выполнения доставки */}
              {((ticket.is_warehouse && (ticket.status === 'recipient_accepted_self_transport' || ticket.status === 'rejected' || ticket.status === 'new' || ticket.status === 'completed' || (ticket.status === 'awaiting_transport' && ticket.can_mark_processed))) ||
                (!ticket.is_warehouse && (ticket.status === 'recipient_accepted_self_transport' || ticket.status === 'rejected' || ticket.status === 'completed' || (ticket.status === 'awaiting_transport' && ticket.can_mark_processed)))) &&
                !ticket.processed_at && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm('Opravdu chcete označit tuto žádost jako zpracovanou?')) {
                      onStatusChange(ticket.id, 'completed')
                    }
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  ✅ Zpracováno
                </button>
              )}
            </div>
          )}
        </div>
        )
      })}
    </div>

    {selectedTicket && (
      <DetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={`Žádost o přesun majetku #${selectedTicket.id.substring(0, 8)}`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {(() => {
              const isRecipient = currentUserId && selectedTicket.assigned_to_id === currentUserId
              
              // Если пользователь одновременно референт маетка и получатель, показываем только кнопки референта
              if (isRecipient && userRole !== 'property_referent' && !selectedTicket.confirmed_received_at && !(selectedTicket as any).recipient_response) {
                return (
                  <>
                    <button
                      onClick={async () => {
                        if (processingTicketId) return
                        setProcessingTicketId(selectedTicket.id)
                        try {
                          await respondToPropertyMoveTicket(selectedTicket.id, 'accepted_self_transport')
                          alert('Děkujeme! Referent majetku byl informován.')
                          onTicketUpdate?.()
                          handleCloseModal()
                        } catch (error) {
                          console.error('Error:', error)
                          alert('Chyba při zpracování odpovědi')
                        } finally {
                          setProcessingTicketId(null)
                        }
                      }}
                      disabled={processingTicketId === selectedTicket.id}
                      style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: processingTicketId === selectedTicket.id ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        opacity: processingTicketId === selectedTicket.id ? 0.6 : 1
                      }}
                    >
                      ✅ Souhlasím přijmout, přesun vyřeším sám
                    </button>
                    <button
                      onClick={async () => {
                        if (processingTicketId) return
                        setProcessingTicketId(selectedTicket.id)
                        try {
                          await respondToPropertyMoveTicket(selectedTicket.id, 'needs_transport')
                          alert('Děkujeme! Referent majetku zorganizuje převoz.')
                          onTicketUpdate?.()
                          handleCloseModal()
                        } catch (error) {
                          console.error('Error:', error)
                          alert('Chyba při zpracování odpovědi')
                        } finally {
                          setProcessingTicketId(null)
                        }
                      }}
                      disabled={processingTicketId === selectedTicket.id}
                      style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: processingTicketId === selectedTicket.id ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        opacity: processingTicketId === selectedTicket.id ? 0.6 : 1
                      }}
                    >
                      🚚 Souhlasím, prosím zorganizovat převoz
                    </button>
                    <button
                      onClick={async () => {
                        if (processingTicketId) return
                        const reason = prompt('Prosím uveďte důvod odmítnutí:')
                        if (!reason) return
                        
                        setProcessingTicketId(selectedTicket.id)
                        try {
                          await respondToPropertyMoveTicket(selectedTicket.id, 'rejected', reason)
                          alert('Vaše odmítnutí bylo odesláno referentovi majetku.')
                          onTicketUpdate?.()
                          handleCloseModal()
                        } catch (error) {
                          console.error('Error:', error)
                          alert('Chyba při zpracování odpovědi')
                        } finally {
                          setProcessingTicketId(null)
                        }
                      }}
                      disabled={processingTicketId === selectedTicket.id}
                      style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: processingTicketId === selectedTicket.id ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        opacity: processingTicketId === selectedTicket.id ? 0.6 : 1
                      }}
                    >
                      ❌ Nesouhlasím přijmout
                    </button>
                  </>
                )
              }
              
              if (userRole === 'property_referent' && onStatusChange) {
                return (
                  <>
                    {/* Кнопка для новой логики - организация отвоза на склад */}
                    {(selectedTicket as any).requester_transport_option === 'organize' && 
                     !selectedTicket.transport_ticket_id && 
                     (selectedTicket.status === 'new' || selectedTicket.status === 'awaiting_recipient_response') && (
                      <button
                        onClick={() => {
                          if (confirm('Opravdu chcete zorganizovat odvoz na sklad?')) {
                            onStatusChange(selectedTicket.id, 'organize_transport')
                            handleCloseModal()
                          }
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        🚚 Zorganizovat odvoz na sklad
                      </button>
                    )}
                    
                    {/* Старая кнопка для recipient_needs_transport */}
                    {selectedTicket.status === 'recipient_needs_transport' && (
                      <button
                        onClick={() => {
                          if (confirm('Opravdu chcete zorganizovat převoz?')) {
                            onStatusChange(selectedTicket.id, 'in_progress')
                            handleCloseModal()
                          }
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        🚚 Zorganizovat převoz
                      </button>
                    )}
                    
                    {/* Кнопка Замитнуть - для всех статусов кроме rejected и completed */}
                    {selectedTicket.status !== 'rejected' && selectedTicket.status !== 'completed' && (
                      <button
                        onClick={() => {
                          if (confirm('Opravdu chcete zamítnout tuto žádost?')) {
                            handleCloseModal()
                            setRejectTicketId(selectedTicket.id)
                            setShowRejectModal(true)
                            setRejectionReason('')
                          }
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        ❌ Zamítnout
                      </button>
                    )}
                    
                    {/* Кнопка Zpracováno - только для заявок на склад ИЛИ после подтверждения получателя ИЛИ после выполнения доставки */}
                    {((selectedTicket.is_warehouse && (selectedTicket.status === 'recipient_accepted_self_transport' || selectedTicket.status === 'rejected' || selectedTicket.status === 'new' || selectedTicket.status === 'completed' || (selectedTicket.status === 'awaiting_transport' && (selectedTicket as any).can_mark_processed))) ||
                      (!selectedTicket.is_warehouse && (selectedTicket.status === 'recipient_accepted_self_transport' || selectedTicket.status === 'rejected' || selectedTicket.status === 'completed' || (selectedTicket.status === 'awaiting_transport' && (selectedTicket as any).can_mark_processed)))) &&
                      !selectedTicket.processed_at && (
                      <button
                        onClick={() => {
                          if (confirm('Opravdu chcete označit tuto žádost jako zpracovanou?')) {
                            onStatusChange(selectedTicket.id, 'completed')
                            handleCloseModal()
                          }
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        ✅ Zpracováno
                      </button>
                    )}
                  </>
                )
              }
              
              return null
            })()}
          </div>
        }
      >
        <div className="details-section">
          <div className="details-grid">
            <div className="details-item">
              <div className="details-label">Status</div>
              <div className="details-value">
                <span className={`status-badge ${getStatusClass(selectedTicket.status)}`}>
                  {getStatusLabel(selectedTicket.status)}
                </span>
              </div>
            </div>
            <div className="details-item">
              <div className="details-label">Žadatel</div>
              <div className="details-value">
                <strong>{selectedTicket.requester_name || 'Neznámý'}</strong>
                {selectedTicket.requester_email && (
                  <div>
                    <a href={`mailto:${selectedTicket.requester_email}`} className="email-link">
                      {selectedTicket.requester_email}
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="details-item">
              <div className="details-label">Od</div>
              <div className="details-value">
                {selectedTicket.from_location || 'Neznámé'}
                {selectedTicket.from_building_name && (
                  <div className="building-name">{selectedTicket.from_building_name}</div>
                )}
              </div>
            </div>
            {selectedTicket.item_name && (
              <div className="details-item">
                <div className="details-label">Název předmětu</div>
                <div className="details-value"><strong>{selectedTicket.item_name}</strong></div>
              </div>
            )}
            <div className="details-item">
              <div className="details-label">Inventární číslo</div>
              <div className="details-value">{selectedTicket.inventory_number || 'Neznámé'}</div>
            </div>
            <div className="details-item">
              <div className="details-label">Kam</div>
              <div className="details-value">
                {selectedTicket.is_warehouse ? (
                  <>
                    <strong>Sklad</strong>
                    {(selectedTicket as any).requester_transport_option && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', fontWeight: 'bold', color: (selectedTicket as any).requester_transport_option === 'organize' ? '#3b82f6' : '#10b981' }}>
                        {(selectedTicket as any).requester_transport_option === 'self' ? '🚗 Žadatel přiveze sám' : '🚚 Požadován odvoz'}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {selectedTicket.to_location || 'Neznámé'}
                    {selectedTicket.to_building_name && (
                      <div className="building-name">{selectedTicket.to_building_name}</div>
                    )}
                  </>
                )}
              </div>
            </div>
            {selectedTicket.assigned_to_name && (
              <div className="details-item">
                <div className="details-label">Komu</div>
                <div className="details-value">
                  {selectedTicket.assigned_to_name}
                  {selectedTicket.assigned_to_email && (
                    <div>
                      <a href={`mailto:${selectedTicket.assigned_to_email}`} className="email-link">
                        {selectedTicket.assigned_to_email}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="details-item">
              <div className="details-label">Vytvořeno</div>
              <div className="details-value">
                {new Date(selectedTicket.created_at).toLocaleString('cs-CZ')}
              </div>
            </div>
            {selectedTicket.confirmed_received_at && (
              <div className="details-item">
                <div className="details-label">Potvrzeno přijetí</div>
                <div className="details-value" style={{ color: '#28a745', fontWeight: 'bold' }}>
                  ✓ {new Date(selectedTicket.confirmed_received_at).toLocaleString('cs-CZ')}
                </div>
              </div>
            )}
            {(selectedTicket as any).recipient_response && (
              <div className="details-item">
                <div className="details-label">Odpověď příjemce</div>
                <div className="details-value" style={{ fontWeight: 'bold' }}>
                  {
                    (selectedTicket as any).recipient_response === 'accepted_self_transport' ? '✅ Přijato, přesun vyřeším sám' :
                    (selectedTicket as any).recipient_response === 'needs_transport' ? '🚚 Přijato, prosím zorganizovat převoz' :
                    (selectedTicket as any).recipient_response === 'rejected' ? '❌ Odmítnuto' : ''
                  }
                </div>
              </div>
            )}
            {(selectedTicket as any).rejection_reason && (
              <div className="details-item">
                <div className="details-label">Důvod odmítnutí</div>
                <div className="details-value" style={{ color: '#dc2626', fontStyle: 'italic' }}>
                  {(selectedTicket as any).rejection_reason}
                </div>
              </div>
            )}
            {selectedTicket.transport_ticket_id && (
              <div className="details-item">
                <div className="details-label">Žádost na IT</div>
                <div className="details-value" style={{ color: '#3b82f6', fontWeight: 'bold' }}>
                  🚚 #{selectedTicket.transport_ticket_id.substring(0, 8)}
                  {selectedTicket.transport_ticket_status && ` (${selectedTicket.transport_ticket_status})`}
                </div>
              </div>
            )}
            {selectedTicket.processed_at && (
              <div className="details-item">
                <div className="details-label">Zpracováno</div>
                <div className="details-value" style={{ color: '#10b981', fontWeight: 'bold' }}>
                  ✅ {new Date(selectedTicket.processed_at).toLocaleString('cs-CZ')}
                </div>
              </div>
            )}
          </div>
        </div>
        {selectedTicket.description && (
          <div className="details-section">
            <div className="details-section-title">Popis</div>
            <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
              {selectedTicket.description}
            </div>
          </div>
        )}
      </DetailsModal>
    )}

    {/* Модалка для отклонения заявки */}
    {showRejectModal && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={handleRejectCancel}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Zamítnout žádost</h2>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Prosím vyplňte důvod zamítnutí žádosti o přesun majetku:
          </p>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Důvod zamítnutí..."
            required
            style={{
              width: '100%',
              minHeight: '120px',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              resize: 'vertical',
              marginBottom: '1rem'
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              onClick={handleRejectCancel}
              disabled={processingTicketId === rejectTicketId}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '4px',
                cursor: processingTicketId === rejectTicketId ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}
            >
              Zrušit
            </button>
            <button
              onClick={handleRejectConfirm}
              disabled={processingTicketId === rejectTicketId || !rejectionReason.trim()}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: processingTicketId === rejectTicketId || !rejectionReason.trim() ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '0.9rem',
                opacity: processingTicketId === rejectTicketId || !rejectionReason.trim() ? 0.6 : 1
              }}
            >
              Zamítnout
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}

export default PropertyMoveTicketList





